/**
 * Report definitions and run snapshots. Pure data shared by the server engine, the web builder and
 * the agent tool. A definition says what one row is (subject and grain), who is in it (scope and
 * filters), what is shown (columns), and how it is summarized (group-by and totals).
 */
import { z } from "zod";
import type { Msg, SubjectKind } from "../../../contract";

export const ENGINE_VERSION = 1;
export const MAX_ROWS = 5000;

export const SUBJECTS = ["animals", "events", "groups", "places", "stock", "tasks", "finance"] as const;
export type ReportSubject = (typeof SUBJECTS)[number];

export const SCOPES = ["current", "former", "reference", "all"] as const;
export type AnimalScope = (typeof SCOPES)[number];

export const GRAINS = ["animal", "event", "animal_event", "group", "place", "lot", "item", "task", "doc", "line"] as const;
export type Grain = (typeof GRAINS)[number];

export const OPS = [
  "eq", "neq", "in", "not_in", "contains", "starts_with",
  "gt", "gte", "lt", "lte", "between",
  "before", "after", "within_last_days", "older_than_days", "within_next_days",
  "is_set", "is_not_set", "is_true", "is_false",
  "exists", "not_exists", "count_gte",
] as const;
export type FilterOp = (typeof OPS)[number];

export const AGGS = ["count", "latest", "first", "sum", "avg", "min", "max"] as const;
export type ColumnAgg = (typeof AGGS)[number];

export const TOTAL_FNS = ["count", "sum", "avg", "min", "max"] as const;
export type TotalFn = (typeof TOTAL_FNS)[number];

export const TOPICS = ["herd", "breeding", "health", "performance", "land", "supplies", "work", "money", "records"] as const;
export type Topic = (typeof TOPICS)[number];

export const TOPIC_LABELS: Record<Topic, string> = {
  herd: "Herd and inventory",
  breeding: "Breeding and calving",
  health: "Health",
  performance: "Weights and performance",
  land: "Places",
  supplies: "Supplies",
  work: "Work and tasks",
  money: "Money",
  records: "Records and exports",
};

/** Validation messages (English catalog keys) so problems read the same in both languages. */
export const ISSUES = {
  grain: "This report can't have that kind of row.",
  future: "The report date can't be in the future.",
  eventType: "That kind of record isn't on this ranch.",
  column: "That column isn't available here.",
  agg: "That summary doesn't fit this column.",
  noAgg: "This column doesn't take a summary.",
  filter: "That filter isn't available here.",
  op: "That way of matching doesn't work with this field.",
  days: "Say how many days.",
  one: "Give a value.",
  many: "Give at least one value.",
  two: "Give a from and a to.",
  field: "That field isn't available here.",
  group: "You can't group by that field.",
  totalColumn: "Add it as a column to total it.",
  totalNumber: "Only numbers can be totaled that way.",
  rate: "Only a records report can show a share of the herd.",
} as const;

export type ValueType = "text" | "tag" | "number" | "date" | "enum" | "quantity" | "money" | "ref" | "refs" | "boolean" | "list" | "msg" | "summary";

/** A filter value: one value, a list, a [from, to] pair, or a quantity with its unit. */
const Scalar = z.union([z.string().max(200), z.number().finite(), z.boolean()]);
const FilterValue = z.union([
  Scalar,
  z.array(Scalar).max(200),
  z.object({ value: z.number().finite(), unit: z.string().max(24) }).strict(),
]);

const DateText = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const FieldId = z.string().min(1).max(160).regex(/^[a-z_][a-z0-9_.]*(:[A-Za-z0-9_.:-]+)?$/, "field");

export const FilterSchema = z.object({
  field: FieldId,
  op: z.enum(OPS),
  value: FilterValue.optional(),
  /** Day count for relative date operators and record windows ("in the last 30 days"). */
  days: z.number().int().min(0).max(36500).optional(),
}).strict();

export const ColumnSchema = z.object({
  field: FieldId,
  /** The person's own heading. Kept as written; never translated. */
  label: z.string().trim().min(1).max(80).optional(),
  agg: z.enum(AGGS).optional(),
  days: z.number().int().min(1).max(36500).optional(),
}).strict();

export const DefinitionSchema = z.object({
  v: z.literal(1),
  subject: z.enum(SUBJECTS),
  scope: z.enum(SCOPES).optional(),
  grain: z.enum(GRAINS).optional(),
  event_types: z.array(z.string().min(3).max(80)).max(60).optional(),
  window: z.object({ days: z.number().int().min(1).max(36500).optional(), from: DateText.optional(), to: DateText.optional() }).strict().optional(),
  filters: z.array(FilterSchema).max(30).default([]),
  columns: z.array(ColumnSchema).min(1).max(40),
  sort: z.array(z.object({ field: FieldId, dir: z.enum(["asc", "desc"]) }).strict()).max(3).optional(),
  group_by: z.array(FieldId).max(2).optional(),
  summary_only: z.boolean().optional(),
  totals: z.array(z.object({ field: FieldId, fn: z.enum(TOTAL_FNS) }).strict()).max(10).optional(),
  as_of: DateText.nullish(),
  blanks: z.array(z.string().trim().min(1).max(40)).max(8).optional(),
  rate: z.literal("herd_at_start").optional(),
  limit: z.number().int().min(1).max(MAX_ROWS).optional(),
}).strict();

export type ReportFilter = z.infer<typeof FilterSchema>;
export type ReportColumn = z.infer<typeof ColumnSchema>;
export type ReportDefinition = z.infer<typeof DefinitionSchema>;

// ---------------------------------------------------------------- run snapshots

export interface RunColumn {
  key: string;
  field: string;
  /** English catalog key, or the person's own heading when `custom` is true. */
  label: string;
  custom?: boolean;
  /** For summary headings like "{{field}}: average", the English label of the field. */
  label_field?: string;
  type: ValueType;
  /** Enum value -> English label key. */
  options?: Record<string, string>;
  /** Display and export unit for quantities (ranch units). */
  unit?: string;
  agg?: ColumnAgg;
  days?: number;
  /** The data behind this column isn't tracked on this ranch yet. */
  unavailable?: boolean;
  /** Ref columns: what the ids point at, for links. */
  ref_kind?: SubjectKind | "member" | "event";
}

export interface RefValue { id: string; label: string; kind?: string }
/** A record's plain-language summary: kept as inputs so each reader sees it in their language. */
export interface SummaryValue {
  summary: { type: string; data: Record<string, unknown>; subjects: Array<{ kind: SubjectKind; id: string; role: string; label?: string }>; occurred_at: string | null; labels: Record<string, string> };
  /** English text, for search, sorting and the agent. */
  text: string;
}
export type Cell = string | number | boolean | null | RefValue | RefValue[] | string[] | { value: number; unit: string } | { minor: number; currency: string } | Msg | SummaryValue;

export interface RunRow {
  key: string;
  /** What the row is about, for links and worklists. */
  subject?: { kind: SubjectKind | "event" | "task"; id: string };
  /** The animal behind the row, when there is one (animal rows and per-animal record rows). */
  animal_id?: string;
  /** Every animal on a record that covers several (one row per record). */
  animal_ids?: string[];
  /** Group keys, matching RunGroup.keys, when the report is grouped. */
  group?: string[];
  cells: Record<string, Cell>;
}

export interface GroupLabel { text: string; translate?: boolean; type?: ValueType | "quantity"; unit?: string }

export interface RunGroup {
  keys: string[];
  labels: GroupLabel[];
  count: number;
  totals: Record<string, { value: number | null; n: number; unit?: string; currency?: string }>;
}

export interface FilterSummary {
  field_label: string;
  custom?: boolean;
  op: FilterOp;
  values: GroupLabel[];
  days?: number;
}

export interface Exclusion {
  filter: FilterSummary;
  removed: number;
  /** Rows left out because the value wasn't recorded (never treated as a match or a miss). */
  unknown: number;
}

export interface Population {
  kind: "animals" | "records" | "groups" | "places" | "lots" | "items" | "tasks" | "documents" | "lines";
  count: number;
  scope?: AnimalScope;
  as_of?: string;
  from?: string | null;
  to?: string | null;
  /** Former animals with no departure date when reconstructing a past inventory. */
  undated?: number;
  /** Records with no date when a date window applies. */
  no_date?: number;
}

export interface RowMeaning {
  grain: Grain | "summary";
  /** English label of the record type when a records report has one type. */
  record?: string;
  /** English labels of group-by fields for summaries. */
  groups?: string[];
}

export interface RunTotals {
  count: number;
  values: Record<string, { value: number | null; n: number; unit?: string; currency?: string }>;
  rate?: { numerator: number; denominator: number; value: number | null; on: string };
}

export interface RunResult {
  subject: ReportSubject;
  as_of: string;
  row_meaning: RowMeaning;
  columns: RunColumn[];
  population: Population;
  exclusions: Exclusion[];
  filters: FilterSummary[];
  notes: Msg[];
  rows: RunRow[];
  groups: RunGroup[] | null;
  totals: RunTotals;
  row_count: number;
  truncated: boolean;
  sensitive: boolean;
}

export interface RunMeta {
  id: string;
  name: string;
  report_id: string | null;
  report_version: number | null;
  template_id: string | null;
  subject: ReportSubject;
  as_of: string;
  timezone: string;
  row_count: number;
  truncated: boolean;
  sensitive: boolean;
  source: "person" | "agent" | "seed";
  created_by: { id: string; label: string } | null;
  created_at: string;
}

export interface RunDTO extends RunMeta, Omit<RunResult, "subject" | "as_of" | "row_count" | "truncated" | "sensitive"> {
  definition: ReportDefinition;
  engine_version: number;
  /** Records changed after this run was made; running again may give a different answer. */
  stale: boolean;
  /** Saved lists made from this report, with what updating them from this run would change. */
  lists: Array<{ id: string; name: string; add: number; remove: number }>;
  previous_run_id: string | null;
}

export interface SavedReportDTO {
  id: string;
  name: string;
  description: string | null;
  topic: Topic;
  subject: ReportSubject;
  definition: ReportDefinition;
  version: number;
  template_id: string | null;
  sensitive: boolean;
  created_by: { id: string; label: string } | null;
  updated_at: string;
  last_run: { id: string; created_at: string; row_count: number } | null;
  can_edit: boolean;
}

export interface TemplateDTO {
  id: string;
  name: string;
  description: string;
  topic: Topic;
  icon: string;
  params: TemplateParam[];
  /** Ready-to-run definition with default parameters. */
  definition: ReportDefinition;
  worklist?: string;
}

export interface TemplateParam {
  key: string;
  label: string;
  kind: "group" | "place" | "days" | "event_types";
  required?: boolean;
  default?: number | string[];
}

export interface LibraryDTO {
  templates: TemplateDTO[];
  saved: SavedReportDTO[];
  recent: RunMeta[];
  can_save: boolean;
}

export interface CompareDTO {
  a: RunMeta;
  b: RunMeta;
  added: Array<{ key: string; label: string; subject?: RunRow["subject"] }>;
  removed: Array<{ key: string; label: string; subject?: RunRow["subject"] }>;
  changed: Array<{ key: string; label: string; subject?: RunRow["subject"]; changes: Array<{ column: RunColumn; before: Cell; after: Cell }> }>;
  unchanged: number;
  groups: Array<{ labels: GroupLabel[]; before: number; after: number }>;
}

export const isRef = (v: unknown): v is RefValue => !!v && typeof v === "object" && !Array.isArray(v) && typeof (v as RefValue).id === "string" && typeof (v as RefValue).label === "string";
export const isQuantity = (v: unknown): v is { value: number; unit: string } => !!v && typeof v === "object" && typeof (v as { value: unknown }).value === "number" && typeof (v as { unit: unknown }).unit === "string";
export const isMoney = (v: unknown): v is { minor: number; currency: string } => !!v && typeof v === "object" && typeof (v as { minor: unknown }).minor === "number" && typeof (v as { currency: unknown }).currency === "string";
export const isSummary = (v: unknown): v is SummaryValue => !!v && typeof v === "object" && !Array.isArray(v) && "summary" in (v as object) && typeof (v as SummaryValue).text === "string";
export const isMsg = (v: unknown): v is Msg => !!v && typeof v === "object" && !Array.isArray(v) && typeof (v as Msg).text === "string" && !("id" in (v as object)) && !("summary" in (v as object));
